import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, catchError, of, Subject, takeUntil } from 'rxjs';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { Event, EventService, Offer, OfferDomain, OfferService } from '../..';
import { OfferPromo } from 'src/app/modules/standard/v1';
import { PromoDomain } from 'src/app/modules/standard/v1';
import { OfferPromoService } from 'src/app/modules/standard/v1';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Status } from '../../models/offer-promo-status';
import { StatusCount } from '../../models/status-count.model';
import { ListViewStateService } from '../../services/list-view-state.service';

const log = new Logger('OffersComponent');

@Component({
  selector: 'app-version-offers',
  templateUrl: './version-offers.component.html',
  styleUrls: ['./version-offers.component.scss']
})

export class VersionOffersComponent<TEvent extends Event, TOfferPromo extends OfferPromo, T extends Offer, TOfferDomain extends OfferDomain<Offer>, TPromoDomain extends PromoDomain<OfferPromo>> implements OnInit, OnDestroy {
  Status: typeof Status = Status;
  public statusCount: StatusCount = {};
  public offerTypeIdsByEventList: any[] = [];
  public currentTypeIndex = -1;
  public showVariantNavigation = false;
  private eventListPageDetails: any;
  private readonly destroy$ = new Subject<void>();
  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  @ViewChild('offerPromoTable', { static: false }) offerPromoTable: any;
  @ViewChild('offerPromoCards', { static: false }) offerPromoCards: any;
  viewModel$ = combineLatest([
    this.eventService.getEvent(this.route.snapshot.params['eventId']),
    this.offerpromoService.isLoading$,
    this.offerpromoService.viewMode$,
    this.offerpromoService.eventId$,
  ]).pipe(
    map(([event, isLoading, viewMode, eventId]) => {
      return { event, isLoading, viewMode, eventId }
    }),
    catchError(error => {
      log.error('Error loading view model', error);
      return of({ event: null, isLoading: false, viewMode: null, eventId: null });
    })
  );

  constructor(
    private eventService: EventService<TEvent>,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    public offerService: OfferService<T, TOfferDomain>,
    private listViewState: ListViewStateService,
  ) { }

  ngOnInit(): void {
    log.debug('init');
    this.offerpromoService.eventId = this.route.snapshot.params['eventId'];
    this.offerService.offerId = this.route.snapshot.params['offerId'];
    this.offerService.reloadOfferVersions();
    this.subscribeToVariantFilter();
    this.initializeNavigation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  backOffers() {
    this.router.navigate([`../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  nextOffer() {
    const offerId = this.route.snapshot.params['offerId'];
    let currentTypeIndex = this.findIndexById(this.offerTypeIdsByEventList, offerId);
    if (currentTypeIndex === -1 || currentTypeIndex >= this.offerTypeIdsByEventList.length - 1) {
      return;
    }

    const targetIndex = currentTypeIndex + 1;
    if (this.shouldLoadNextPage(currentTypeIndex)) {
      this.loadPageIfNeeded(targetIndex, () => this.navigateByIndex(targetIndex));
      return;
    }

    this.navigateByIndex(targetIndex);
  }

  previousOffer() {
    const offerId = this.route.snapshot.params['offerId'];
    let currentTypeIndex = this.findIndexById(this.offerTypeIdsByEventList, offerId);
    if (currentTypeIndex === -1 || currentTypeIndex <= 0) {
      return;
    }

    const targetIndex = currentTypeIndex - 1;
    if (this.shouldLoadPreviousPage(currentTypeIndex)) {
      this.loadPageIfNeeded(targetIndex, () => this.navigateByIndex(targetIndex));
      return;
    }

    this.navigateByIndex(targetIndex);
  }

  private initializeNavigation() {
    this.eventListPageDetails = JSON.parse(JSON.stringify(this.offerpromoService.getPageDetails()));
    this.offerTypeIdsByEventList = [];
    const existingTypeData = this.offerpromoService.getTypeIdsByEventInitialData();
    if (existingTypeData) {
      this.offerTypeIdsByEventList = existingTypeData;
      this.currentTypeIndex = this.findIndexById(this.offerTypeIdsByEventList, this.route.snapshot.params['offerId']);
      return;
    }

    this.SearchAllOfferCopyIdsByEvent();
  }

  private SearchAllOfferCopyIdsByEvent(onPageLoaded?: () => void): void {
    if (!this.eventListPageDetails) {
      return;
    }

    this.offerpromoService.SearchAllOfferCopyIdsByEvent(this.eventListPageDetails).subscribe({
      next: (res: { '@odata.count': number; value: any[] }) => {
        const totalRecords = res['@odata.count'];
        const eventOfferTypeIds = res.value.map((item) => this.normalizeOfferType(item));

        if (!this.offerTypeIdsByEventList || this.offerTypeIdsByEventList.length !== totalRecords) {
          this.offerTypeIdsByEventList = Array(totalRecords).fill(null);
        }

        this.offerTypeIdsByEventList.splice(
          this.eventListPageDetails.pageIndex * this.eventListPageDetails.pageSize,
          eventOfferTypeIds.length,
          ...eventOfferTypeIds
        );
        onPageLoaded?.();

        const offerId = this.route.snapshot.params['offerId'];
        const index = this.findIndexById(this.offerTypeIdsByEventList, offerId);

        if (index > -1) {
          this.currentTypeIndex = index;
          if (this.shouldLoadNextPage(this.currentTypeIndex)) {
            this.loadPageIfNeeded(this.currentTypeIndex + 1);
          } else if (this.shouldLoadPreviousPage(this.currentTypeIndex)) {
            this.loadPageIfNeeded(this.currentTypeIndex - 1);
          }
        } else if (this.eventListPageDetails.pageIndex < Math.ceil(totalRecords / this.eventListPageDetails.pageSize)) {
          this.eventListPageDetails.pageIndex++;
          this.SearchAllOfferCopyIdsByEvent(onPageLoaded);
        }
      },
      error: (error) => {
        log.error('Error fetching event offer type IDs', error);
      }
    });
  }

  private shouldLoadNextPage(currentIndex: number): boolean {
    return this.offerTypeIdsByEventList[currentIndex + 1] == null &&
      currentIndex + 1 < this.offerTypeIdsByEventList.length;
  }

  private shouldLoadPreviousPage(currentIndex: number): boolean {
    return this.offerTypeIdsByEventList[currentIndex - 1] == null &&
      currentIndex - 1 >= 0;
  }

  private loadPageIfNeeded(targetIndex: number, onPageLoaded?: () => void) {
    const pageIndex = this.getPageIndex(targetIndex, this.eventListPageDetails.pageSize);
    if (pageIndex < 0 || pageIndex * this.eventListPageDetails.pageSize >= this.offerTypeIdsByEventList.length) {
      return;
    }

    this.eventListPageDetails.pageIndex = pageIndex;
    this.SearchAllOfferCopyIdsByEvent(onPageLoaded);
  }

  private navigateByIndex(targetIndex: number): void {
    const targetType = this.offerTypeIdsByEventList[targetIndex];
    if (!targetType) {
      return;
    }

    this.navigateToNextItem(targetType?.Label, targetType?.VariantIds?.[0] || targetType?.BaseId, targetIndex);
  }

  private normalizeOfferType(item: any): any {
    if (item?.BaseId != null || item?.baseId != null || item?.VariantIds != null || item?.variantIds != null) {
      return {
        BaseId: item.BaseId ?? item.baseId,
        Label: item.Label ?? item.label,
        VariantIds: item.VariantIds ?? item.variantIds ?? [],
      };
    }
    return this.offerpromoService.transformOfferToVariantList(item);
  }

  private subscribeToVariantFilter(): void {
    this.offerpromoService.filters$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters: any[]) => {
        const hasVariantsInFilters = filters?.some((filter) =>
          filter?.fieldName?.toLowerCase() === 'hasvariants' &&
          (filter?.value === true || filter?.value === 'true')
        );

        const filterSelections = this.listViewState.listViewStateValue.filters;
        this.showVariantNavigation = !!hasVariantsInFilters || !!filterSelections?.['withVariants'];
      });
  }

  private findIndexById(list: any[], value: string): number {
    return list.findIndex((item) => item?.BaseId === value || item?.VariantIds?.includes(value));
  }

  private getPageIndex(itemPosition: number, pageSize: number): number {
    if (pageSize <= 0) {
      return -1;
    }

    return Math.floor(itemPosition / pageSize);
  }

  private navigateToNextItem(type: string, id: string, targetIndex: number): void {
    const currentPath = this.route.snapshot.routeConfig?.path;
    const isInNestedSubRoute = currentPath === 'promo/childversions';
    const routePrefix = isInNestedSubRoute ? '../../../' : '../../';
    const typeRouteMap: { [key: string]: string[] } = {
      offer: [routePrefix, id, 'childversions'],
      promo: [routePrefix, id, 'promo', 'childversions']
    };

    const route = typeRouteMap[(type || '').toLowerCase()];
    if (!id || !route) {
      return;
    }

    this.router.navigate(route, { relativeTo: this.route, queryParamsHandling: 'preserve' }).then((allowed) => {
      if (allowed) {
        this.currentTypeIndex = targetIndex;
        if ((type || '').toLowerCase() === 'offer') {
          this.offerService.offerId = id;
          this.offerService.reloadOfferVersions();
        }
      }
    });
  }

  getStatusCount() {
    this.offerpromoService.getStatusCount().subscribe((count) => {
      this.statusCount = count;
    })
  }

  toggleView(mode: string) {
    this.offerpromoService.toggleViewMode(mode);
  }

  deleteSelectedRecords() {
    let selectedRecordsDetails: any;
    if (this.offerPromoTable) {
      selectedRecordsDetails = this.offerPromoTable.getSelectedSectionRecords();
    } else if (this.offerPromoCards) {
      selectedRecordsDetails = this.offerPromoCards.getSelectedSectionRecords();
    }
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      const variantNamesArr = selectedRecordsDetails.map(
        (x: any) => x.Detail.OfferVariantName
      );

      const count = variantNamesArr.length;
      const variantNames = count <= 3 ? variantNamesArr.join(', ') : `${variantNamesArr.slice(0, 2).join(', ')} +${count - 2} more`;
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: count === 1 ? `Are you sure you want to delete ${variantNamesArr[0]}?` : `Are you sure you want to delete ${count} variants [${variantNames}]?`,
        },
        disableClose: true,
      });
      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            const selectedRecords = selectedRecordsDetails.map((x: any) => { x.EventId = this.route.snapshot.params['eventId']; return x.Detail; });
            const hasBaseVariant = selectedRecords.some((item: any) => item.OfferVariantName === "Base");
            this.offerpromoService.deleteOfferPromos(selectedRecords).subscribe({
              next: () => {
                this.matSnackBar.open(count === 1 ? `Variant "${variantNamesArr[0]}" deleted successfully` : `${count} variants deleted successfully`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                if (hasBaseVariant) {
                  this.router.navigate([`../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
                } else {
                  this.offerService.reloadOfferVersions();
                }
                if (this.offerPromoTable) {
                  this.offerPromoTable.clearSelection();
                } else if (this.offerPromoCards) {
                  this.offerPromoCards.clearSelection();
                }
              },
              error: (error) => {
                log.error('Error in deleting variant(s) ', error);
                this.matSnackBar.open(`Failed to delete variant(s): ${error.message}`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              }
            });
          }
        });
    }
  }

  updateStatus(status: Status) {
    let selectedRecordsDetails: any;
    if (this.offerPromoTable) {
      selectedRecordsDetails = JSON.parse(JSON.stringify(this.offerPromoTable.getSelectedSectionRecords()));
    } else if (this.offerPromoCards) {
      selectedRecordsDetails = JSON.parse(JSON.stringify(this.offerPromoCards.getSelectedSectionRecords()));
    }
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      selectedRecordsDetails = selectedRecordsDetails.map((x: any) => {
        let obj = {
          Id: x.Detail.Id,
          Label: x.EventOfferType,
          Status: status as unknown as string
        }
        return obj;
      });
      this.offerpromoService.updateStatus(selectedRecordsDetails).subscribe({
        next: () => {
          this.offerService.reload();
          this.offerpromoService.reload();
          this.offerService.reloadOfferVersions();
          if (this.offerPromoTable) {
            this.offerPromoTable.clearSelection();
          } else if (this.offerPromoCards) {
            this.offerPromoCards.clearSelection();
          }
          this.getStatusCount();
        },
        error: (error) => {
          log.error('Error in updating status', error);
          this.matSnackBar.open(`Error in updating status: ${error.message}`, 'OK', DEFAULT_SNACKBAR_CONFIG);
        }
      });
    }
  }
}
